import React, { useState } from 'react';
import {
  Tooltip,
  IconButton,
  Box,
  Typography,
  Button,
  Popover,
  Card,
  CardContent,
  CardActions,
} from '@mui/material';
import {
  Help as HelpIcon,
  HelpOutline as HelpOutlineIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useHelp } from '../../contexts/HelpContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useMobileTopBarAction, useMobileTopBarActions } from './MobileTopBarActionsContext';

interface HelpTooltipProps {
  helpId: string;
  title: string;
  content: string | React.ReactNode;
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end';
  size?: 'small' | 'medium' | 'large';
  variant?: 'icon' | 'inline';
  maxWidth?: number;
}

const HelpTooltip: React.FC<HelpTooltipProps> = ({
  helpId,
  title,
  content,
  placement = 'top',
  size = 'small',
  variant = 'icon',
  maxWidth = 600
}) => {
  const { isHelpEnabled, disableHelp } = useHelp();
  const { t } = useLanguage();
  const mobileTopBarActions = useMobileTopBarActions();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const helpEnabled = isHelpEnabled(helpId);

  useMobileTopBarAction(helpEnabled && mobileTopBarActions ? {
    id: helpId,
    label: title,
    icon: <HelpOutlineIcon fontSize="small" />,
    order: 100,
    kind: 'help',
    onSelect: setAnchorEl,
  } : null);

  if (!helpEnabled) {
    return null;
  }

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDisableHelp = () => {
    disableHelp(helpId);
    handleClose();
  };

  const open = Boolean(anchorEl);

  const iconSize = size === 'small' ? 'small' : size === 'large' ? 'large' : 'medium';

  const helpControl = variant === 'inline' ? (
      <>
        {!mobileTopBarActions && <IconButton
          size={iconSize}
          onClick={handleClick}
          aria-label={title}
          aria-haspopup="dialog"
          aria-expanded={open}
          sx={{ ml: 0.5, verticalAlign: 'middle', color: 'primary.main' }}
        >
          <HelpIcon fontSize={iconSize} />
        </IconButton>}
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: placement.includes('top') ? 'top' : 'bottom',
            horizontal: placement.includes('start') ? 'left' : placement.includes('end') ? 'right' : 'center',
          }}
          transformOrigin={{
            vertical: placement.includes('top') ? 'bottom' : 'top',
            horizontal: placement.includes('start') ? 'left' : placement.includes('end') ? 'right' : 'center',
          }}
        >
          <Card role="dialog" aria-label={title} sx={{ maxWidth: maxWidth }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2" color="primary" fontWeight="bold">
                  {title}
                </Typography>
                <IconButton aria-label={t('common.close')} size="small" onClick={handleClose} sx={{ ml: 1, mt: -0.5, minWidth: 40, minHeight: 40 }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box sx={{ color: 'text.secondary' }}>
                {typeof content === 'string' ? (
                  <Typography variant="body2">{content}</Typography>
                ) : (
                  content
                )}
              </Box>
            </CardContent>
            <CardActions sx={{ pt: 0 }}>
              <Button size="small" onClick={handleDisableHelp} color="secondary">
                {t('help.dontShowAgain')}
              </Button>
              <Button size="small" onClick={handleClose} variant="contained">
                {t('help.gotIt')}
              </Button>
            </CardActions>
          </Card>
        </Popover>
      </>
    ) : (
    <>
      {!mobileTopBarActions && <Tooltip title={title} placement={placement}>
        <IconButton
          size={iconSize}
          onClick={handleClick}
          aria-label={title}
          aria-haspopup="dialog"
          aria-expanded={open}
          sx={{ color: 'primary.main' }}
        >
          <HelpIcon fontSize={iconSize} />
        </IconButton>
      </Tooltip>}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: placement.includes('top') ? 'top' : 'bottom',
          horizontal: placement.includes('start') ? 'left' : placement.includes('end') ? 'right' : 'center',
        }}
        transformOrigin={{
          vertical: placement.includes('top') ? 'bottom' : 'top',
          horizontal: placement.includes('start') ? 'left' : placement.includes('end') ? 'right' : 'center',
        }}
      >
        <Card role="dialog" aria-label={title} sx={{ maxWidth: maxWidth }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2" color="primary" fontWeight="bold">
                {title}
              </Typography>
              <IconButton aria-label={t('common.close')} size="small" onClick={handleClose} sx={{ ml: 1, mt: -0.5, minWidth: 40, minHeight: 40 }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box sx={{ color: 'text.secondary' }}>
              {typeof content === 'string' ? (
                <Typography variant="body2">{content}</Typography>
              ) : (
                content
              )}
            </Box>
          </CardContent>
          <CardActions sx={{ pt: 0 }}>
            <Button size="small" onClick={handleDisableHelp} color="secondary">
              {t('help.dontShowAgain')}
            </Button>
            <Button size="small" onClick={handleClose} variant="contained">
              {t('help.gotIt')}
            </Button>
          </CardActions>
        </Card>
      </Popover>
    </>
  );

  return helpControl;
};

export default HelpTooltip;
